/**
 * DeployModelConfirmGate — the single mount (``HorizontalNavBar``) behind
 * ``confirmModelDeployment()``. On SaaS with billing on it opens
 * ``DeployModelConfirmModal`` and answers the pending request with the user's
 * choice; elsewhere it answers ``true`` without rendering anything.
 */
import React, { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/features/auth/AuthContext';
import { MODEL_DEPLOY_CONFIRM_EVENT } from '../utils/modelDeployConsent';
import DeployModelConfirmModal from './DeployModelConfirmModal';

export default function DeployModelConfirmGate() {
    const { isSaas, billingEnabled } = useAuth() || {};
    const gated = isSaas === true && billingEnabled === true;
    const gatedRef = useRef(gated);
    gatedRef.current = gated;
    const [pending, setPending] = useState(null);

    useEffect(() => {
        const onRequest = (event) => {
            const detail = event?.detail;
            if (!detail || typeof detail.resolve !== 'function') return;
            detail.handled = true;
            if (!gatedRef.current) {
                detail.resolve(true);
                return;
            }
            setPending((current) => {
                // A second request while one is open is declined rather than queued.
                if (current) {
                    detail.resolve(false);
                    return current;
                }
                return detail;
            });
        };
        window.addEventListener(MODEL_DEPLOY_CONFIRM_EVENT, onRequest);
        return () => window.removeEventListener(MODEL_DEPLOY_CONFIRM_EVENT, onRequest);
    }, []);

    const answer = (accepted) => {
        setPending((current) => {
            current?.resolve(accepted);
            return null;
        });
    };

    if (!pending) return null;
    return <DeployModelConfirmModal open onConfirm={() => answer(true)} onCancel={() => answer(false)} />;
}
